import React, { useState, useContext, useCallback } from 'react'
import { Modal, Spin, Button } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { useGameLeaderboard } from '../../services/api/recharge-type-hooks'
import { useAppSelector } from '../../store/hooks'
import { selectUserIdSlice } from '../../store/user-id-slice'

import { ThemeContext } from '../../context/themeContext'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import IntlMessages from '../../util/IntlMessages'

import { ActiveTab } from './RechargeTypeGame'
import './styles.less'

interface RechargeTypeLeaderboardProps {
  visible: boolean
  onClose: () => void
  onPlayGame?: () => void
  limit?: number
}


export const RechargeTypeLeaderboard: React.FC<RechargeTypeLeaderboardProps> = ({
  visible,
  onClose,
  onPlayGame,
  limit = 10,
}) => {
  const { formatMessage } = useIntl()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === DARK_THEME
  const { userId } = useAppSelector(selectUserIdSlice)

  // TanStack Query hook
  const { data, isLoading, error, refetch } = useGameLeaderboard(limit, {
    enabled: visible !== false, // Fetch when modal is visible (defaults to true if not specified)
    refetchOnMount: true, // Always refetch when component mounts/becomes visible to ensure fresh data
  })

  const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.LEADERBOARD)
  const [currentStreak] = useState(0) // Display 0 when viewing leaderboard (not in active game)
  const bestStreak = data?.userStats?.bestStreak ?? 0

  const loadLeaderboard = useCallback(() => {
    void refetch()
  }, [refetch])

  const handleClose = () => {
    onClose()
  }

  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab)
    if (tab === 'play' && onPlayGame) {
      handleClose()
      onPlayGame()
    }
  }

  const getRankClass = (rank: number, isCurrentUser: boolean) => {
    if (isCurrentUser) return 'you'
    if (rank === 1) return 'gold'
    if (rank === 2) return 'silver'
    if (rank === 3) return 'bronze'
    return ''
  }

  const renderHeader = () => (
    <div className="game-modal-header">
      <div className="header-title">
        <span className="header-emoji">🎯</span>
        <span className="header-text">
          <IntlMessages id="rechargeType.game.title" />
        </span>
      </div>
      <button className="header-close" onClick={handleClose}>
        <CloseOutlined />
      </button>
    </div>
  )

  const renderStreakBar = () => (
    <div className="game-streak-bar">
      <div className="streak-left">
        <span className="streak-label">
          <IntlMessages id="rechargeType.game.currentStreak" />
        </span>
        <div className="streak-value">
          <span className="streak-fire">🔥</span>
          <span className="streak-number">{currentStreak}</span>
        </div>
      </div>
      <div className="streak-right">
        <span className="streak-label">
          <IntlMessages id="rechargeType.game.yourBest" />
        </span>
        <span className="streak-best">{bestStreak}</span>
      </div>
    </div>
  )

  const renderTabBar = () => (
    <div className="game-tab-bar">
      <button
        className={`tab-item ${activeTab === ActiveTab.PLAY ? 'active' : ''}`}
        onClick={() => handleTabChange(ActiveTab.PLAY)}
      >
        <span className="tab-icon">🎮</span>
        <span className="tab-label">
          <IntlMessages id="rechargeType.game.tabPlay" />
        </span>
      </button>
      <button
        className={`tab-item ${activeTab === ActiveTab.LEADERBOARD ? 'active' : ''}`}
        onClick={() => handleTabChange(ActiveTab.LEADERBOARD)}
      >
        <span className="tab-icon">🏆</span>
        <span className="tab-label">
          <IntlMessages id="rechargeType.game.tabLeaderboard" />
        </span>
      </button>
    </div>
  )

  const renderContent = () => {
    // Show loading only if we don't have cached data
    if (isLoading && !data) {
      return (
        <div className="game-loading">
          <Spin size="large" />
        </div>
      )
    }

    if (error) {
      return (
        <div className="game-content leaderboard-error">
          <div className="error-icon">😕</div>
          <h3><IntlMessages id="rechargeType.leaderboard.loadError" /></h3>
          <Button type="primary" onClick={loadLeaderboard}>
            <IntlMessages id="app.tryAgain" />
          </Button>
        </div>
      )
    }

    if (!data || data.leaderboard.length === 0) {
      return (
        <div className="game-content leaderboard-empty">
          <div className="empty-icon">🏆</div>
          <h3><IntlMessages id="rechargeType.leaderboard.noStreaks" /></h3>
          <p><IntlMessages id="rechargeType.leaderboard.beFirst" /></p>
          {onPlayGame && (
            <Button type="primary" onClick={onPlayGame}>
              <IntlMessages id="rechargeType.game.startPlaying" />
            </Button>
          )}
        </div>
      )
    }

    return (
      <div className="game-content leaderboard-content">
        <div className="leaderboard-title">
          <span className="title-icon">🏆</span>
          <span className="title-text">
            <IntlMessages id="rechargeType.leaderboard.title" />
          </span>
        </div>

        <div className="leaderboard-list">
          {data.leaderboard.map((entry) => {
            const isCurrentUser = entry.userId === userId
            const rankClass = getRankClass(entry.rank, isCurrentUser)

            return (
              <div
                key={entry.userId}
                className={`leaderboard-row ${rankClass}`}
              >
                <div className="row-rank">
                  <span className={`rank-number ${rankClass}`}>{entry.rank}</span>
                </div>
                <div className="row-avatar">
                  {isCurrentUser ? (
                    <div className="you-avatar">
                      {formatMessage({ id: 'rechargeType.leaderboard.you' })}
                    </div>
                  ) : (
                    <UserAvatar
                      id={entry.userId}
                      name={entry.name}
                      avatar={entry.imageUrl}
                      avatarSize={40}
                      shape="circle"
                    />
                  )}
                </div>
                <div className="row-info">
                  <span className="row-name">
                    {isCurrentUser
                      ? formatMessage({ id: 'rechargeType.leaderboard.you' })
                      : entry.name
                    }
                  </span>
                </div>
                <div className="row-score">
                  <span className="score-value">{entry.bestStreak}</span>
                  <span className="score-label">
                    <IntlMessages id="rechargeType.leaderboard.streak" />
                  </span>
                </div>
              </div>
            )
          })}
        </div>

        <Button
          type="primary"
          size="large"
          onClick={() => {
            handleClose()
            onPlayGame?.()
          }}
          className="play-again-btn"
        >
          <IntlMessages id="rechargeType.game.playAgain" />
        </Button>
      </div>
    )
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={480}
      centered
      closable={false}
      maskClosable={false}
      className={`recharge-game-modal-v2 ${isDark ? 'dark' : ''}`}
    >
      <div className="game-modal-container">
        {renderHeader()}
        {(!isLoading || data) && !error && renderStreakBar()}
        {renderContent()}
        {(!isLoading || data) && !error && renderTabBar()}
      </div>
    </Modal>
  )
}
